import { useEffect, useState } from 'react'

import { Modal, Divider, Text, useModalStackContext } from '@mantine/core'
import dayjs from 'dayjs'

import Button from 'src/components/Buttons/Button/Button'
import DateInput from 'src/components/Inputs/DateInput/DateInput'
import { HiringStage } from 'src/graphql/types/applicants'
import { formatSnakeValue } from 'src/lib/formatters'

const STACK_ID = 'reenter-prospect-modal'

interface ReenterProspectModalProps {
  hiringStage: HiringStage
  onSave: (values: {
    applicantStage: string
    hiringStage: HiringStage
    applicantStageDate: Date
  }) => void
  onClose: () => void
}

const ReenterProspectModal = ({
  hiringStage,
  onSave,
  onClose,
}: ReenterProspectModalProps) => {
  const stackCtx = useModalStackContext()

  // Mantine gap: Modal's stack-register useEffect has no cleanup, so
  // unmounting while opened leaks the stackId in Modal.Stack. Mirrors DeleteModal.
  useEffect(() => {
    return () => {
      if (stackCtx) {
        stackCtx.removeModal(STACK_ID)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date())

  const isToday = selectedDate
    ? dayjs(selectedDate).isSame(dayjs(), 'day')
    : true

  const handleConfirm = () => {
    onSave({
      applicantStage: 'PROSPECT',
      hiringStage,
      applicantStageDate: selectedDate || new Date(),
    })
    onClose()
  }

  return (
    <Modal
      opened={true}
      onClose={onClose}
      classNames={{ title: 'text-lg font-bold' }}
      title="Re-enter Prospect Stage"
      centered
      stackId={STACK_ID}
    >
      <div className="grid grid-cols-2 gap-4">
        <Text className="col-span-2 text-sm text-doubleNickel-gray-600">
          This applicant will be moved back to the Prospect stage. Their
          application date will be updated to{' '}
          {isToday
            ? "today's date."
            : `${dayjs(selectedDate).format('MM/DD/YYYY')}.`}
        </Text>
        <div className="col-span-2">
          <DateInput
            label="Application Date"
            value={selectedDate}
            onChange={(date: Date | null) => setSelectedDate(date)}
          />
        </div>
        <Text className="col-span-2 text-sm font-medium text-doubleNickel-gray-700">
          Hiring sub-stage: {formatSnakeValue(hiringStage)}
        </Text>
        <Divider className="col-span-2" />
        <Button text="Cancel" variant="outline" onClick={onClose} />
        <Button text="Confirm" onClick={handleConfirm} />
      </div>
    </Modal>
  )
}

export default ReenterProspectModal
